import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Chip,
  Divider,
  Rating,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Breadcrumbs,
  Link,
  Tab,
  Tabs
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Icons
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import VerifiedIcon from '@mui/icons-material/Verified';
import BuildIcon from '@mui/icons-material/Build';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
}));

const GreenButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#006938',
  color: 'white',
  '&:hover': {
    backgroundColor: '#004d2b',
  },
  borderRadius: theme.spacing(0.5),
  textTransform: 'none',
  fontWeight: 600
}));

const PartDetails = () => {
  const { id } = useParams();
  const [part, setPart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  
  // Mock part data
  const mockPart = {
    id: 1,
    name: 'iPhone 13 OLED Screen',
    category: 'screen',
    brand: 'Apple',
    price: 129.99,
    rating: 4.5,
    reviewCount: 128,
    image: 'https://via.placeholder.com/600x400?text=iPhone+13+Screen',
    compatibility: ['iPhone 13', 'iPhone 13 Pro'],
    inStock: true,
    quantity: 25,
    description: 'Original quality replacement screen for iPhone 13. This OLED display provides vibrant colors and deep blacks, matching the quality of the original screen. The assembly includes the front glass, digitizer, and OLED panel.',
    features: [
      'Original OLED quality',
      'Includes digitizer and touch functionality',
      'Pre-installed adhesive strips',
      'Compatible with Face ID',
      '1-year warranty'
    ],
    specifications: {
      'Resolution': '2532 x 1170 pixels',
      'Technology': 'OLED',
      'Size': '6.1 inches',
      'Color': 'Black',
      'Condition': 'New'
    }
  };
  
  useEffect(() => {
    // Simulate loading data from API
    setTimeout(() => {
      setPart(mockPart);
      setLoading(false);
    }, 1000);
  }, [id]);
  
  // Handle quantity change
  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (value > 0 && value <= part.quantity) {
      setQuantity(value);
    }
  };
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Typography>Loading part details...</Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 4 }}
        >
          <Link 
            underline="hover" 
            color="inherit" 
            href="/"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link 
            underline="hover" 
            color="inherit" 
            href="/parts"
          >
            Parts
          </Link>
          <Link 
            underline="hover" 
            color="inherit" 
            href={`/parts?category=${part.category}`}
          >
            {part.category.charAt(0).toUpperCase() + part.category.slice(1)}
          </Link>
          <Typography color="text.primary">{part.name}</Typography>
        </Breadcrumbs>
        
        {/* Product Details */}
        <Grid container spacing={4}>
          {/* Product Image */}
          <Grid item xs={12} md={6}>
            <StyledPaper>
              <Box
                component="img"
                src={part.image}
                alt={part.name}
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 1
                }}
              />
            </StyledPaper>
          </Grid>
          
          {/* Product Info */}
          <Grid item xs={12} md={6}>
            <StyledPaper>
              <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#006938', fontWeight: 700 }}>
                {part.name}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Rating value={part.rating} precision={0.1} readOnly />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  ({part.reviewCount} reviews)
                </Typography>
              </Box>
              
              <Typography variant="h5" color="#006938" fontWeight="bold" sx={{ mb: 2 }}>
                ${part.price.toFixed(2)}
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                {part.inStock ? (
                  <Chip 
                    icon={<CheckCircleIcon />} 
                    label="In Stock" 
                    color="success" 
                    variant="outlined" 
                  />
                ) : (
                  <Chip 
                    label="Out of Stock" 
                    color="error" 
                    variant="outlined" 
                  />
                )}
              </Box>
              
              <Typography variant="body1" paragraph>
                {part.description}
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Compatible with:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {part.compatibility.map((device, index) => (
                    <Chip
                      key={index}
                      label={device}
                      sx={{ bgcolor: 'rgba(0, 105, 56, 0.1)', color: '#006938' }}
                    />
                  ))}
                </Box>
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={4}>
                  <TextField
                    label="Quantity"
                    type="number"
                    value={quantity}
                    onChange={handleQuantityChange}
                    inputProps={{ min: 1, max: part.quantity }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={8}>
                  <GreenButton
                    variant="contained"
                    startIcon={<AddShoppingCartIcon />}
                    fullWidth
                    size="large"
                    disabled={!part.inStock}
                  >
                    Add to Cart
                  </GreenButton>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3 }}>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <LocalShippingIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Free shipping on orders over $50" 
                      secondary="Estimated delivery: 2-4 business days" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <VerifiedIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Genuine parts guarantee" 
                      secondary="All parts are tested and verified" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <BuildIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Installation support available" 
                      secondary="Access repair guides or book a technician" 
                    />
                  </ListItem>
                </List>
              </Box>
            </StyledPaper>
          </Grid>
        </Grid>
        
        {/* Product Details Tabs */}
        <StyledPaper sx={{ mt: 4 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
                '& .Mui-selected': { color: '#006938' },
                '& .MuiTabs-indicator': { backgroundColor: '#006938' }
              }}
            >
              <Tab label="Features" />
              <Tab label="Specifications" />
              <Tab label="Installation Guide" />
            </Tabs>
          </Box>
          
          <Box sx={{ py: 3 }}>
            {tabValue === 0 && (
              <List>
                {part.features.map((feature, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: '#006938' }} />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
            )}
            
            {tabValue === 1 && (
              <Grid container spacing={2}>
                {Object.entries(part.specifications).map(([key, value], index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: 'flex', borderBottom: '1px solid #eee', py: 1 }}>
                      <Typography variant="body1" fontWeight="bold" sx={{ width: '40%' }}>
                        {key}:
                      </Typography>
                      <Typography variant="body1" sx={{ width: '60%' }}>
                        {value}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
            
            {tabValue === 2 && (
              <Box>
                <Alert severity="info" sx={{ mb: 3 }}>
                  This part requires careful installation. We recommend following our step-by-step guide or consulting a professional.
                </Alert>
                <Typography variant="body1" paragraph>
                  For detailed installation instructions, please visit our Repair Guide section or watch our installation video.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button variant="outlined" color="primary" href="/repair-guide">
                    View Repair Guide
                  </Button>
                  <Button variant="outlined" color="primary">
                    Watch Video Tutorial
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </StyledPaper>
      </Container>
    </Box>
  );
};

export default PartDetails; 